import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Site, CanalCreatedData } from './screens.types';

type Dispo = 'disponible' | 'occupé' | 'en-route';

interface Membre {
  id: string;
  nom: string;
  role: string;
  site: string;
  dispo: Dispo;
}

@Component({
  selector: 'app-canal-screen',
  standalone: true,
  imports: [FormsModule, TranslatePipe],
  templateUrl: './canal-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./canal-screen.component.scss'],
})
export class CanalScreenComponent implements OnChanges {
  @Input({ required: true }) site!: Site;
  @Input() filterRoles: string[] = [];
  @Input() filterSites: string[] = [];
  @Input() preselectedNom: string | null = null;
  @Output() openFilter = new EventEmitter<void>();
  @Output() canalCreated = new EventEmitter<CanalCreatedData>();

  canalName = '';
  readonly search = signal('');
  readonly selectedIds = signal<string[]>([]);

  readonly allMembres: Membre[] = [
    { id: 'm1', nom: 'Sophie M.',  role: 'Biologiste',             site: 'Nantes Centre',  dispo: 'occupé'      },
    { id: 'm2', nom: 'Paul R.',    role: 'Responsable accueil',    site: 'Carquefou',      dispo: 'disponible'  },
    { id: 'm3', nom: 'Claire B.',  role: 'Infirmière',             site: 'Saint-Herblain', dispo: 'occupé'      },
    { id: 'm4', nom: 'Marc D.',    role: 'Directeur opérationnel', site: 'Réseau',         dispo: 'disponible'  },
    { id: 'm5', nom: 'Léa T.',     role: 'RAQ',                    site: 'Rezé',           dispo: 'disponible'  },
    { id: 'm6', nom: 'Antoine F.', role: 'Technicien de labo',     site: 'Orvault',        dispo: 'en-route'    },
    { id: 'm7', nom: 'Marie L.',   role: 'Infirmière',             site: 'Rezé',           dispo: 'disponible'  },
    { id: 'm8', nom: 'Julie K.',   role: 'Biologiste',             site: 'Carquefou',      dispo: 'en-route'    },
  ];

  dispoLabel(d: Dispo): string {
    return ({ disponible: 'Disponible', 'occupé': 'Occupé', 'en-route': 'En route' })[d];
  }

  readonly roles = [...new Set(this.allMembres.map(m => m.role))];
  readonly sites = [...new Set(this.allMembres.map(m => m.site))];

  get hasActiveFilter(): boolean {
    return this.filterRoles.length > 0 || this.filterSites.length > 0;
  }

  get filteredMembres(): Membre[] {
    const q = this.search().toLowerCase().trim();
    return this.allMembres.filter(m => {
      if (this.filterRoles.length > 0 && !this.filterRoles.includes(m.role)) return false;
      if (this.filterSites.length > 0 && !this.filterSites.includes(m.site)) return false;
      if (!q) return true;
      return m.nom.toLowerCase().includes(q) || m.role.toLowerCase().includes(q) || m.site.toLowerCase().includes(q);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['site']) {
      const now = new Date();
      const date = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}`;
      const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const raw = `${this.site.nom} : ${date} ${time}`;
      this.canalName = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    }
    if (changes['preselectedNom'] && this.preselectedNom) {
      const membre = this.allMembres.find(m => m.nom === this.preselectedNom);
      if (membre) this.selectedIds.set([membre.id]);
    }
  }

  toggleMembre(id: string) {
    const current = this.selectedIds();
    this.selectedIds.set(current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  }

  isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  tensionKey(): string {
    return { critique: 'critique', moderee: 'tension', fluide: 'fluide', ferme: 'fluide' }[this.site.statut];
  }

  tensionLabel(): string {
    return { critique: 'Critique', moderee: 'En tension', fluide: 'Fluide', ferme: 'Fermé' }[this.site.statut];
  }

  create() {
    if (!this.canalName.trim()) return;
    const ids = this.selectedIds();
    const selected = this.allMembres.filter(m => ids.includes(m.id));
    const tl = this.site.timeline;
    const firstIdx = tl.findIndex(c => c !== 'vert');
    const tensionMinutes = firstIdx !== -1 ? (5 - firstIdx) * 15 : null;
    this.canalCreated.emit({
      name: this.canalName.trim(),
      siteStatut: this.site.statut,
      memberCount: selected.length,
      memberNames: selected.map(m => m.nom),
      presenceEstimee: this.site.presenceEstimee,
      tensionMinutes,
    });
  }
}